/* global APP_VERSION */

import { useAuth } from '@context/AuthContext';
import { useAppSelector } from '@store/index';
import { usePostHog } from 'posthog-js/react';
import { useEffect } from 'react';
import { identify } from '@utils/analytics/telemetry';
import { TelemetryProfile } from '@utils/analytics/types';

const getAppVersion = (): string => {
  try {
    return APP_VERSION;
  } catch {
    return '';
  }
};

const getModulesCount = (): number => {
  try {
    const raw = localStorage.getItem('user_modules');
    if (!raw) return 0;
    const parsed = JSON.parse(raw) as unknown[];
    return Array.isArray(parsed) ? parsed.length : 0;
  } catch {
    return 0;
  }
};

// Identifica al usuario en PostHog + Clarity + GA4 con un payload enriquecido y consistente.
// Se re-ejecuta cuando llegan/cambian los datos del store (suscripción/comercio cargan tras
// login por fetch) y en cada sesión — esto resuelve el bug del gate que dejaba las sesiones
// de Clarity anónimas a partir de la 2ª recarga.
const useTelemetryIdentify = (): void => {
  const auth = useAuth();
  const posthog = usePostHog();
  const subscription = useAppSelector((state) => state.subscription);
  const commerce = useAppSelector((state) => state.commerce);

  useEffect(() => {
    if (!auth.isAuthenticated || !auth.isReady) return;

    const idUser = auth.userId || localStorage.getItem('id_user') || '';
    if (!idUser && !auth.commerceName) return;

    const profile: TelemetryProfile = {
      idUser,
      idCommerce: localStorage.getItem('id_commerce'),
      idPath: auth.commercePath || localStorage.getItem('id_path'),
      commerceName: auth.commerceName,
      email: auth.user?.email,
      name: auth.user?.name,
      userName: auth.userName,
      userStatus: auth.user?.status,
      userType: auth.userType,
      isRoot: auth.isRoot,
      userModulesCount: getModulesCount(),
      onboardingCompleted: Boolean(auth.onboardingDate),
      planId: subscription.planId,
      trialDaysRemaining: subscription.trialDaysRemaining,
      trialUsed: subscription.trialUsed,
      quotaUsagePercent: subscription.orderLimits?.quotaUsagePercent ?? null,
      businessType: commerce?.business_type ?? null,
      businessSubType: commerce?.business_sub_type ?? null,
      countryCode: typeof commerce?.country_code === 'number' ? String(commerce.country_code) : null,
      timezone: commerce?.zona_horaria || null,
      registeredAt: commerce?.fecha_registro ?? null,
      appVersion: getAppVersion(),
    };

    identify(posthog, profile).catch(() => {
      // noop — la identificación no debe romper el render
    });
    // Dependemos de los objetos `subscription` y `commerce` (referencias estables de Redux,
    // cambian solo cuando el slice se actualiza) para re-identificar con TODOS los campos
    // enriquecidos cuando llegan/cambian, no solo un subconjunto.
  }, [
    auth.isAuthenticated,
    auth.isReady,
    auth.userId,
    auth.commerceName,
    auth.userType,
    auth.isRoot,
    auth.onboardingDate,
    auth.userName,
    auth.user?.email,
    auth.user?.status,
    subscription,
    commerce,
    posthog,
  ]);
};

export default useTelemetryIdentify;
